import React, { useState, ChangeEvent } from 'react';
import ReactDOM from 'react-dom';
import './styles.scss';
import { IFilepicker } from 'app/manage/filepicker';

declare global {
  interface Window {
    FileReader: any;
  }
}

declare const filepicker: IFilepicker;

interface ImportUsersFormProps {
  processImportUrl: string;
  downloadTemplateUrl: string;
  filestackKey: string;
  setImportStatus: Function;
  setImportFailure: () => void;
  trialPage: boolean;
}

export function ImportUsersForm({
  processImportUrl,
  downloadTemplateUrl,
  filestackKey,
  setImportStatus,
  setImportFailure,
  trialPage,
}: ImportUsersFormProps) {
  const noFileMessage = 'No file chosen';

  const [filename, setFilename] = useState(noFileMessage);
  const [fileError, setFileError] = useState<string | null>();

  function clearState() {
    setFilename(noFileMessage);
    setFileError(null);
  }

  // add on-close callback to make sure we clear out the state
  $('#modal-import-users').on('hidden', function () {
    clearState();
  });

  function trackTemplateClick() {
    window.analyticsClient.then((analyticsClient: any) => {
      analyticsClient.sendUIEvent({
        actionSubject: 'button',
        actionSubjectId: 'downloadUserImportTemplate',
        action: 'clicked',
        source: 'audience',
      });
    });
  }

  function trackImportClick() {
    window.analyticsClient.then((analyticsClient: any) => {
      analyticsClient.sendUIEvent({
        actionSubject: 'button',
        actionSubjectId: 'importUsers',
        action: 'clicked',
        source: 'audience',
      });
    });
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.files || !event.target.files[0]) {
      // User cancelled
      return;
    } else {
      setFileError(null);
      setFilename(event.target.files[0].name);
    }
  }

  function onSubmit() {
    if (trialPage) return;

    trackImportClick();

    const fileInput = ReactDOM.findDOMNode(
      document.querySelector('#csv_file'),
    ) as HTMLFormElement;

    if (!fileInput.files || fileInput.files.length == 0) {
      setFileError('Please provide a CSV file to import.');
      return;
    }

    // Check the size of the file to make sure it's not too large if the browser supports the check
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (window.FileReader && fileInput.files[0].size > maxSize) {
      setFileError("You can't upload files larger than 5MB.");
      return;
    }

    // Good to upload! Let's close the modal and kick off file processing.
    clearState();
    $('#close-import-users-btn').trigger('click');

    // Upload the file to Filestack. Once it's uploaded, kick off import processing.
    filepicker.setKey(filestackKey);
    filepicker.store(
      fileInput,
      { mimetype: 'text/csv' },
      (FPFile) => {
        const filepickerId = FPFile.url.split('/').pop() as string;
        processImportFile(filepickerId);
      },
      () => {
        setImportFailure();
      },
      () => {},
    );
  }

  function processImportFile(filepickerId: string) {
    if (!filepickerId) {
      setImportFailure();
      return;
    }

    $.ajax({
      type: 'POST',
      url: processImportUrl,
      dataType: 'json',
      data: {
        filepicker_id: filepickerId,
      },
      success: (data) => setImportStatus(data),
      error: () => setImportFailure(),
    });
  }

  function maybeRenderDisabledMessage() {
    if (trialPage) {
      return (
        <div className="cpt-notification warning in-page disabled-warning">
          Importing users is disabled on trial pages. Upgrade to a paid plan to
          enable this feature.
        </div>
      );
    }
  }

  function maybeRenderFileError() {
    if (fileError) {
      return <div className="cpt-notification error in-page">{fileError}</div>;
    }
  }

  function renderFileInput() {
    if (trialPage) {
      return (
        <div className="file-upload-container">
          <button className="cpt-button style-primary disabled">
            Choose file
          </button>
          {filename}
        </div>
      );
    } else {
      return (
        <div className="file-upload-container">
          <label htmlFor="csv_file">Choose file</label>
          <input
            type="file"
            accept="text/csv"
            id="csv_file"
            name="csv_file"
            onChange={handleChange}
            onKeyPress={(e) => {
              // default causes browser to open file explorer when enter is pressed
              e.preventDefault();
            }}
          />
          {filename}
        </div>
      );
    }
  }

  return (
    <div
      className="modal hide fade modal-import-users"
      id="modal-import-users"
      style={{ display: 'none' }}
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          onSubmit();
        }
      }}
    >
      <div className="content">
        <div className="header">
          <h3 className="modal-title">Import users</h3>
          <p>Add new users to your audience-specific page in bulk.</p>
        </div>
        {maybeRenderDisabledMessage()}
        <div className="body form-horizontal">
          <ol className="styled">
            <li>
              Download the{' '}
              <a
                href={downloadTemplateUrl}
                target="_blank"
                onClick={trackTemplateClick}
              >
                CSV template
              </a>
              .
            </li>
            <li>
              Add user details and access to the template.
              <small className="footnote">
                If you include existing users, we won’t make changes to their
                data.
              </small>
            </li>
            <li>
              Upload CSV file.
              <small className="footnote">
                5MB file size limit, 500 user limit, xlsx not supported.
              </small>
            </li>
          </ol>
        </div>
        {maybeRenderFileError()}
        {renderFileInput()}
        <div className="modal-footer">
          <div className="learn-more">
            <a
              target="_blank"
              href="https://help.statuspage.io/help/import-audience-specific-users"
            >
              Learn more
            </a>
          </div>
          <div>
            <a
              href="#"
              data-dismiss="modal"
              className="close hide"
              id="close-import-users-btn"
            >
              Cancel
            </a>
            <button
              className={`cpt-button style-primary ${
                trialPage ? 'disabled' : ''
              }`}
              id="btn-import-users"
              onClick={onSubmit}
            >
              Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportUsersForm;
